/* eslint-disable @typescript-eslint/no-explicit-any */
import {Injectable, Pipe, PipeTransform} from '@angular/core';
import {TraitsService} from '../../../common/service/traits.service';
import {LookupService} from '../../../common/service/lookup.service';
import {FeatureAttributesLookup} from '../../../common/models/feature-attributes.model';
import {VestingPurposeLookup} from '../../../common/models/vesting-purpose-model';
import {equals} from '../../../common/service/data-util';


const purposeMap = new Map<string, string>();

@Pipe({
    name: 'PurposeReFormat',
    standalone: true
})
export class PurposeReFormatPipe implements PipeTransform {
    transform(value: any): string {
        if(!value) return '';
        const match = value.match(/^\[(\d+)\](?:-.*)?$/);
        if(!match) return value;
        return purposeMap.get(match[1]) ?? value;
    }
}

@Pipe({
    name: 'FreeTextPurposeReFormat',
    standalone: true
})
export class FreeTextPurposeReFormatPipe implements PipeTransform {
    transform(value: any): string {
        if(!value) return '';
        const match = value.match(/^\[\d+\]-(.+)/);
        if(match) return match[1];
        return '';
    }
}

@Injectable({
    providedIn: 'root'
})
export class AttributeViewerColumnService {
    title = 'AttributeViewerColumnService';

    constructor(
        private lookupService: LookupService,
        private traitsService: TraitsService
    ) {}

    populateColumns(context: any, setupTreeColumn: boolean) {
        console.debug(`${this.title} - populateColumns`);

        const featureAttributesLookups: FeatureAttributesLookup[] = this.lookupService.getLookups('featureAttributes');

        if (setupTreeColumn) {
            // Tree expand / collapse column
            context.options.columns.push({
                isTreeColumn: true,
                width: 60,
                filterable: false,
                name: '',
                treeFromRelation: 'transientParentId',
                treeToRelation: 'transientId'
            });
        }

        (featureAttributesLookups.some(l => l.planTraitCriteria)
            ? this.filterLookupValuesByPlanTraitCriteria(featureAttributesLookups)
            : featureAttributesLookups
        ).forEach(l => {
            if (
                (l.featureType === context.tool.type && l.featureClassAttribute === true) ||
                (l.featureType === 'point' && context.tool.type === 'controlPoint' && l.featureClassAttribute === true)
            ) {
                if (l.display === true) {
                    this.addColumn(context.options, l);
                }
                context.attributes.push(l);
            }
        });

        const numColumns = context.options.columns.length;
        context.sorts.splice(
            0,
            context.sorts.length,
            ...(context.options.columns as {prop: 'string'; sort: 'string'; sortPriority: number}[])
                .filter(column => column.sort)
                .sort((c1, c2) => {
                    const sortPriority1 = c1.sortPriority || numColumns;
                    const sortPriority2 = c2.sortPriority || numColumns;
                    return sortPriority1 - sortPriority2;
                })
                .map(column => ({prop: column.prop, dir: column.sort}))
        );
    }

    showOrHideColumn(options: any, attribute: any) {
        console.debug(`${this.title} - showOrHideColumn. Columns selected size: ${options.columns.length}`);

        if (attribute.hideColumn) {
            attribute.display = !attribute.display;

            if (attribute.display) {
                this.addColumn(options, attribute);
            } else {
                this.removeColumn(options, attribute);
            }
        }
    }

    removeColumn(options: any, attribute: any) {
        console.debug(`${this.title} - removeColumn`);

        options.columns = options.columns.filter((column: any) => !equals(column.prop, attribute.prop));

        this.organiseColumns(options);
    }

    addColumn(options: any, attribute: any) {
        console.debug(`${this.title} - addColumn`);

        attribute.width = attribute.width !== null && attribute.width !== 0 ? attribute.width : 100;
        attribute.checkboxable = options.columns.length === 0;
        attribute.headerCheckboxable = options.columns.length === 0;
        const sortTokens = attribute.sort ? attribute.sort.split(':') : [];
        attribute.sort = sortTokens.length ? sortTokens[0] : undefined;
        attribute.sortPriority = sortTokens.length >= 2 ? parseInt(sortTokens[1]) : undefined;
        attribute.filterable = false;

        attribute.className = attribute.cellAlign === 'left' ? 'text-left' : 'text-right';
        attribute.headerClassName = attribute.cellAlign === 'left' ? 'text-left' : 'text-right';



        if(attribute.name === 'Purpose' && attribute.lookupService === 'vestingPurposeLookups'){
            const vestingPurposeLookups: VestingPurposeLookup[] = this.lookupService.getLookups('vestingPurpose');
            vestingPurposeLookups.forEach(l => {
                purposeMap.set(l.id, l.description);
            })
            attribute = {...attribute, pipe: new PurposeReFormatPipe()}
            options.columns = [...options.columns, attribute];
        }else if(attribute.name === 'Free Text Purpose'){
            attribute = {...attribute, pipe: new FreeTextPurposeReFormatPipe()}
            options.columns = [...options.columns, attribute];
        }else{
            options.columns = [...options.columns, attribute];
        }

        this.organiseColumns(options);
    }

    organiseColumns(options: any) {
        console.debug(`${this.title} - organiseColumns`);

        if (options.columns.length == 1) {
            const firstColumn = options.columns[0];
            firstColumn.checkboxable = true;
            firstColumn.headerCheckboxable = true;
        } else {
            options.columns.forEach((column: any) => {
                column.checkboxable = false;
                column.headerCheckboxable = false;
            });

            this.orderColumns(options);

            const firstColumn = options.columns[0];
            firstColumn.checkboxable = true;
            firstColumn.headerCheckboxable = true;
        }
    }

    orderColumns(options: any) {
        console.debug(`${this.title} - orderColumns`);

        options.columns.sort((a: any, b: any) => parseInt(a.displayOrder) - parseInt(b.displayOrder));
    }

    private filterLookupValuesByPlanTraitCriteria(featureAttributesLookups: FeatureAttributesLookup[]) {
        return featureAttributesLookups.filter(l => this.traitsService.criteriaMatches(l.planTraitCriteria, true));
    }
}
